import Preferences, { DashboardLayout as SavedLayout } from './preferences.js';
import Toast from './toast.js';

/**
 * Dashboard customisation: which cards show and in what order. Cards are the `.dash-widget`
 * elements in dashboard.html, identified by `data-widget`; the saved layout is just an ordered
 * id list plus a hidden list (users.uiPreferences via Preferences). Cards added in later releases
 * (ids not in a saved order) are appended in their template position, visible unless the
 * template marks them `data-default="hidden"`.
 *
 * Reordering uses pointer events rather than HTML5 drag-and-drop so it works with touch too.
 */
export default abstract class DashboardLayout {
	private static grid: HTMLElement | null = null;
	/** Template order + default visibility, captured before any saved layout is applied. */
	private static defaults: { id: string; hidden: boolean }[] = [];
	/** Layout to restore on Cancel. */
	private static snapshot: SavedLayout | null = null;

	public static async init() {
		const grid = document.getElementById('dashboard-grid');
		if (!grid) { return; }
		DashboardLayout.grid = grid;

		DashboardLayout.defaults = DashboardLayout.widgets().map(w => ({ id: w.dataset.widget!, hidden: w.dataset.default === 'hidden' }));
		DashboardLayout.widgets().forEach(w => DashboardLayout.decorate(w));

		const prefs = await Preferences.get();
		DashboardLayout.apply(prefs.dashboardLayout ?? null);

		document.getElementById('dashboard-customise')?.addEventListener('click', () => DashboardLayout.enterEdit());
		document.getElementById('dashboard-edit-cancel')?.addEventListener('click', () => DashboardLayout.exitEdit(false));
		document.getElementById('dashboard-edit-save')?.addEventListener('click', () => DashboardLayout.exitEdit(true));
		document.getElementById('dashboard-edit-reset')?.addEventListener('click', () => DashboardLayout.apply(null));
	}

	private static widgets(): HTMLElement[] {
		return DashboardLayout.grid ? Array.from(DashboardLayout.grid.querySelectorAll<HTMLElement>(':scope > .dash-widget')) : [];
	}

	/** Current layout as it stands in the DOM. */
	private static current(): SavedLayout {
		const widgets = DashboardLayout.widgets();
		return {
			order: widgets.map(w => w.dataset.widget!),
			hidden: widgets.filter(w => w.classList.contains('dash-hidden')).map(w => w.dataset.widget!),
		};
	}

	/** Apply a saved layout (null = template default). */
	private static apply(layout: SavedLayout | null) {
		const grid = DashboardLayout.grid;
		if (!grid) { return; }
		const byId = new Map(DashboardLayout.widgets().map(w => [w.dataset.widget!, w]));

		let order: string[];
		let hidden: Set<string>;
		if (layout) {
			const known = layout.order.filter(id => byId.has(id));
			// New cards the saved layout predates: slot them in after their template predecessor.
			const missing = DashboardLayout.defaults.filter(d => !known.includes(d.id));
			order = [...known];
			for (const m of missing) {
				const defaultIdx = DashboardLayout.defaults.findIndex(d => d.id === m.id);
				const prev = DashboardLayout.defaults.slice(0, defaultIdx).reverse().find(d => order.includes(d.id));
				order.splice(prev ? order.indexOf(prev.id) + 1 : 0, 0, m.id);
			}
			hidden = new Set([...layout.hidden.filter(id => byId.has(id)), ...missing.filter(m => m.hidden).map(m => m.id)]);
		} else {
			order = DashboardLayout.defaults.map(d => d.id);
			hidden = new Set(DashboardLayout.defaults.filter(d => d.hidden).map(d => d.id));
		}

		for (const id of order) {
			const w = byId.get(id)!;
			grid.appendChild(w);
			DashboardLayout.setHidden(w, hidden.has(id));
		}
	}

	private static setHidden(w: HTMLElement, hidden: boolean) {
		w.classList.toggle('dash-hidden', hidden);
		const toggle = w.querySelector<HTMLButtonElement>('.dash-visibility');
		if (toggle) {
			toggle.textContent = hidden ? '+ Show' : '✕';
			toggle.title = hidden ? `Show ${w.dataset.title}` : `Hide ${w.dataset.title}`;
			toggle.setAttribute('aria-label', toggle.title);
		}
	}

	/** Add the edit-mode tools (drag handle, show/hide) to a card. */
	private static decorate(w: HTMLElement) {
		const tools = document.createElement('div');
		tools.className = 'dash-widget-tools';

		const handle = document.createElement('button');
		handle.type = 'button';
		handle.className = 'dash-drag-handle';
		handle.textContent = '⠿';
		handle.title = `Drag to move ${w.dataset.title}`;
		handle.setAttribute('aria-label', handle.title);
		handle.addEventListener('pointerdown', e => DashboardLayout.startDrag(e, w));
		// Keyboard reordering: arrow keys move the card one place.
		handle.addEventListener('keydown', e => {
			if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); DashboardLayout.moveBy(w, -1); handle.focus(); }
			if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); DashboardLayout.moveBy(w, 1); handle.focus(); }
		});

		const visibility = document.createElement('button');
		visibility.type = 'button';
		visibility.className = 'dash-visibility';
		visibility.addEventListener('click', e => {
			e.stopPropagation();
			DashboardLayout.setHidden(w, !w.classList.contains('dash-hidden'));
		});

		tools.append(handle, visibility);
		w.appendChild(tools);

		const label = document.createElement('div');
		label.className = 'dash-hidden-label';
		label.textContent = 'Hidden — click to show';
		w.appendChild(label);

		// In edit mode, clicking anywhere on a greyed-out card adds it back.
		w.addEventListener('click', () => {
			if (DashboardLayout.grid?.classList.contains('editing') && w.classList.contains('dash-hidden')) {
				DashboardLayout.setHidden(w, false);
			}
		});

		DashboardLayout.setHidden(w, false);
	}

	private static moveBy(w: HTMLElement, delta: number) {
		const list = DashboardLayout.widgets();
		const idx = list.indexOf(w);
		const target = list[idx + delta];
		if (!target) { return; }
		if (delta < 0) { target.before(w); } else { target.after(w); }
	}

	private static startDrag(e: PointerEvent, w: HTMLElement) {
		if (!DashboardLayout.grid?.classList.contains('editing')) { return; }
		e.preventDefault();

		const rect = w.getBoundingClientRect();
		const ghost = w.cloneNode(true) as HTMLElement;
		ghost.classList.add('dash-drag-ghost');
		ghost.querySelectorAll('canvas').forEach(c => c.remove());
		ghost.style.width = `${rect.width}px`;
		ghost.style.height = `${rect.height}px`;
		ghost.style.left = `${rect.left}px`;
		ghost.style.top = `${rect.top}px`;
		document.body.appendChild(ghost);
		document.body.classList.add('dash-drag-active');
		w.classList.add('dash-dragging');

		const offsetX = e.clientX - rect.left;
		const offsetY = e.clientY - rect.top;

		const onMove = (ev: PointerEvent) => {
			ghost.style.left = `${ev.clientX - offsetX}px`;
			ghost.style.top = `${ev.clientY - offsetY}px`;

			// Auto-scroll near the viewport edges so cards can travel the whole page.
			if (ev.clientY < 60) { window.scrollBy(0, -12); }
			else if (ev.clientY > window.innerHeight - 60) { window.scrollBy(0, 12); }

			const under = document.elementFromPoint(ev.clientX, ev.clientY)?.closest<HTMLElement>('.dash-widget');
			if (!under || under === w || under.parentElement !== DashboardLayout.grid) { return; }
			const r = under.getBoundingClientRect();
			// Left half of the card under the pointer drops before it, right half after it.
			if (ev.clientX < r.left + r.width / 2) { under.before(w); } else { under.after(w); }
		};

		const onUp = () => {
			document.removeEventListener('pointermove', onMove);
			document.removeEventListener('pointerup', onUp);
			document.removeEventListener('pointercancel', onUp);
			ghost.remove();
			document.body.classList.remove('dash-drag-active');
			w.classList.remove('dash-dragging');
		};

		document.addEventListener('pointermove', onMove);
		document.addEventListener('pointerup', onUp);
		document.addEventListener('pointercancel', onUp);
	}

	private static enterEdit() {
		const grid = DashboardLayout.grid;
		if (!grid || grid.classList.contains('editing')) { return; }
		DashboardLayout.snapshot = DashboardLayout.current();
		grid.classList.add('editing');
		document.getElementById('dashboard-edit-bar')?.classList.remove('hidden');
		document.getElementById('dashboard-customise')?.classList.add('hidden');
		// Charts inside newly-shown cards need a resize tick.
		window.dispatchEvent(new Event('resize'));
	}

	private static async exitEdit(save: boolean) {
		const grid = DashboardLayout.grid;
		if (!grid) { return; }

		if (save) {
			const layout = DashboardLayout.current();
			const saved = await Preferences.save({ dashboardLayout: layout });
			if (!saved) {
				Toast.show('Could not save your layout — please try again.', 'error');
				return;
			}
			Toast.show('Dashboard layout saved', 'success', 2500);
		} else if (DashboardLayout.snapshot) {
			DashboardLayout.apply(DashboardLayout.snapshot);
		}

		grid.classList.remove('editing');
		document.getElementById('dashboard-edit-bar')?.classList.add('hidden');
		document.getElementById('dashboard-customise')?.classList.remove('hidden');
		window.dispatchEvent(new Event('resize'));
	}
}
